import { Descriptions, Drawer, List, Spin, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { DiagnosticRuleDetail } from '~/app/services/diagnosticRules/diagnostic-rule.types';
import { IndexingStatusTag } from '../IndexingStatusTag/IndexingStatusTag';

const { Paragraph, Text } = Typography;

export type RuleDetailDrawerProps = {
  open: boolean;
  detail: DiagnosticRuleDetail | null;
  isLoading: boolean;
  onClose: () => void;
};

export function RuleDetailDrawer({
  open,
  detail,
  isLoading,
  onClose,
}: RuleDetailDrawerProps) {
  const { t, i18n } = useTranslation('diagnosticRules');
  const date = (value: string | Date) =>
    new Date(value).toLocaleString(i18n.language);

  return (
    <Drawer
      open={open}
      width={640}
      onClose={onClose}
      title={detail?.title ?? t('detail.title')}
    >
      {isLoading || !detail ? (
        <Spin />
      ) : (
        <>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label={t('columns.kind')}>
              {t(`kinds.${detail.kind}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('columns.version')}>
              {detail.currentVersion}
            </Descriptions.Item>
            <Descriptions.Item label={t('columns.indexing')}>
              <IndexingStatusTag rule={detail} />
            </Descriptions.Item>
            <Descriptions.Item label={t('columns.active')}>
              {detail.isActive ? t('values.yes') : t('values.no')}
            </Descriptions.Item>
            {detail.fileName ? (
              <Descriptions.Item label={t('form.document')}>
                {detail.fileName}
              </Descriptions.Item>
            ) : null}
          </Descriptions>
          <Typography.Title level={5} style={{ marginTop: 24 }}>
            {t('detail.versions')}
          </Typography.Title>
          <List
            dataSource={detail.versions}
            renderItem={(version) => {
              const { snapshot } = version;
              return (
                <List.Item>
                  <List.Item.Meta
                    title={
                      <>
                        <Tag>v{version.version}</Tag>
                        <Text strong>{snapshot.title}</Text>
                      </>
                    }
                    description={
                      <>
                        <Paragraph ellipsis={{ rows: 3, expandable: true }}>
                          {snapshot.description}
                        </Paragraph>
                        <Text type="secondary">{date(version.createdAt)}</Text>
                      </>
                    }
                  />
                </List.Item>
              );
            }}
          />
        </>
      )}
    </Drawer>
  );
}
